import { Dialog, SidebarCheckpoint } from "@components";
import { ApplicationLayout, NotFoundState, Sidebar, Spinner } from "@rio-cloud/rio-uikit";
import { useAppSelector } from "@store";
import { CheckpointStep, CheckpointStop, DriverTripDetailsResponse, SocialUser, StageCheckpointStop } from "@types";
import { formatLocalDate, getDriverIdFromUrl, getTripIdFromUrl, mountPolyline } from "@utils";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getBoundingBox, IRoutePoint, ISandboxMarker } from "src/components/Map";
import { decode } from "src/utils/flexiblePolyline";
import RioMap from "../../components/Map";
import SidebarTripDetail from "../../components/SidebarTripDetail";
import { MapDrawingProvider } from "../../providers/MapDrawingContext";
import { getFollowers } from "../../services/social";
import { fetchTripDetailsByTripId } from "../../services/trips";
import { getCheckpointDetailByIdV2 } from "src/services/checkpoint";
import { useHandleRoutes } from "@hooks";
import { Position } from "@rio-cloud/rio-uikit/mapTypes";

interface LocationState {
  driverId?: string;
  tripId?: string;
  page?: number;
  status?: string;
}

const PerformanceScreen = () => {

  const navigate = useNavigate();
  const { driverId: paramDriver, tripId: paramTrip } = useParams<{
    driverId: string;
    tripId: string;
  }>();

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Deep-link (query param) tem prioridade sobre o state interno: permite abrir a
  // tela de detalhes direto pela URL, ex.: .../trip?tab=1&trip-id=<uuid>&driver-id=<rioId>.
  // O `trip-id`/`driver-id` (hifenizados) ficam dentro do hash por causa do hash routing.
  const tripId = paramTrip ?? getTripIdFromUrl() ?? searchParams.get("tripId") ?? state?.tripId;
  const driverId = paramDriver ?? getDriverIdFromUrl() ?? searchParams.get("driverId") ?? state?.driverId;
  // O endpoint de detalhes (v2/routes/trips/details/{rioId}/trips/{tripId}) precisa
  // do motorista (driverId/rioId) além do tripId. Num deep-link só com `trip-id`,
  // o driverId fica ausente: deixamos isso explícito ao invés de mostrar um painel
  // vazio/enganoso (ver early-return abaixo).
  const isMissingDriver = !!tripId && !driverId;
  const [view, setView] = useState<"DEFAULT" | "DETAILS">("DETAILS");
  const [closed, setClosed] = useState(false);
  const profile = useAppSelector((state) => state.login.userProfile);
  const userIdAmity = useAppSelector((state) => state.login.userIdAmity);
  const [tripDetails, setTripDetails] = useState<DriverTripDetailsResponse | null>(null);
  const [segments, setSegments] = useState<IRoutePoint[]>([]);
  const [markers, setMarkers] = useState<ISandboxMarker[]>([]);
  const [currencySegments, setCurrencySegments] = useState<IRoutePoint[]>([]);
  const [currencyMarker, setCurrencyMarker] = useState<ISandboxMarker>();
  const [position, setPosition] = useState<Position | undefined>(undefined);
  const [boundingBox, setBoundingBox] = useState<{
    top: number;
    bottom: number;
    left: number;
    right: number;
  }>();

  //const driverId = searchParams.get("driverId") ?? state?.driverId;
  //const tripId = searchParams.get("tripId") ?? state?.tripId;
  const [usersAmity, setUsersAmity] = useState<Amity.User[]>([]);
  const [followers, setFollowers] = useState<SocialUser[]>([]);
  const [open, setOpen] = useState(true);
  const [openStages, setOpenStages] = useState(false);
  const [checkpoint, setCheckpoint] = useState<CheckpointStop>();
  const [stage, setStage] = useState<CheckpointStep>();
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const { handleGenerateMarker, handleRoutes } = useHandleRoutes();
  const [zoom, setZoom] = useState(10);

  const loadTripDetails = async (driverId: string, tripId: string) => {
    setLoading(true)
    try {
      const response = await fetchTripDetailsByTripId(driverId, tripId);
      setTripDetails(response);
      setView("DETAILS");
      setClosed(false);
    } catch (err) {
      console.error("Erro ao buscar detalhes da viagem:", err);
    } finally {
      // Sempre encerra o loading — senão o Spinner de tela cheia trava na falha.
      setLoading(false);
    }
  };

  const handleCurrencyPolyline = () => {
    if (!tripDetails?.positions) return;

    const positions = tripDetails.positions?.reverse()
    const lastLocation = positions[positions.length - 1];

    if (lastLocation) {
      setCurrencyMarker(handleGenerateMarker({
        lat: lastLocation?.lat,
        lng: lastLocation?.lng,
        mode: 'street_truck',
      }));
    }
    setCurrencySegments(positions);
  }

  const handleRoutePolyline = () => {
    const {
      boundingBox,
      markers,
      position,
      segments,
      zoom
    } = handleRoutes(tripDetails?.responseHere?.routes as any, tripDetails?.routes?.stops as any)
    setSegments(segments);
    setMarkers(markers);
    setPosition(position);
    setZoom(zoom);
    
    if (boundingBox) {
      setBoundingBox({
        top: boundingBox?.top + 0.4,
        bottom: boundingBox?.bottom - 0.1,
        left: boundingBox?.left - 0.4,
        right: boundingBox?.right + 0.4,
      });
    }
  };

  useEffect(() => {
    if (!userIdAmity || userIdAmity === "undefined") return;

    const fetchUsers = async () => {
      try {
        const res = await getFollowers(userIdAmity);
        setUsersAmity(res.data.result.users);
        setFollowers(res.data.result.follows);
      } catch (err) {
        console.error("Erro ao buscar followers:", err);
      }
    };

    fetchUsers();
  }, [userIdAmity]);

  useEffect(() => {
    if (driverId && tripId) {
      try {
        loadTripDetails(driverId, tripId);
      } catch {
        console.log("Erro ao carregar detalhes da viagem");
      }
    }
  }, [driverId, tripId]);

  useEffect(() => {
    if (tripDetails?.responseHere?.routes) {
      handleRoutePolyline();
    }
  }, [tripDetails]);

  useEffect(() => {
    if (tripDetails?.positions && tripDetails.positions.length > 1) {
      handleCurrencyPolyline();
    }
  }, [tripDetails?.polyline])

  // Deep-link só com `trip-id` (sem `driver-id`): o endpoint de detalhes precisa
  // do motorista, então deixamos a limitação explícita em vez de um painel
  // "VIAGEM CANCELADA" vazio e enganoso.
  if (isMissingDriver) {
    return (
      <div className="display-flex align-items-center justify-content-center width-100pct height-100pct">
        <NotFoundState
          headline="Link incompleto"
          message={
            "Para abrir os detalhes desta viagem é necessário o identificador do motorista " +
            "(driver-id) na URL, além do trip-id."
          }
          condensed
          buttons={[
            {
              text: (
                <>
                  <span className="rioglyph rioglyph-close" />
                  <span>Sair</span>
                </>
              ),
              className: "width-200",
              bsStyle: "default",
              onClick: () => navigate("/routing/performance/list"),
            },
          ]}
        />
      </div>
    );
  }

  // Cabeçalho do painel: início e fim reais da viagem, com hora.
  const tripDetailsTitle = [
    "Detalhes da viagem",
    tripDetails?.routes?.startDateTime &&
      `Iniciada em ${formatLocalDate(tripDetails.routes.startDateTime, "DD/MM/YYYY [às] HH:mm")}`,
    tripDetails?.routes?.endDateTime &&
      `Finalizada em ${formatLocalDate(tripDetails.routes.endDateTime, "DD/MM/YYYY [às] HH:mm")}`,
  ]
    .filter(Boolean)
    .join(" - ");

  return (
    <>
      <Spinner show={loading} text='Buscando detalhes da rota' isFullScreen />
      <ApplicationLayout.Sidebar className="right z-index-2 height-100pct">
        <Sidebar
          title={
            tripDetails?.routes?.endDateTime
              ? tripDetailsTitle
              : state?.status === "INTERRUPTED"
                ? "VIAGEM INTERROMPIDA"
                : state?.status
                  ? "VIAGEM CANCELADA"
                  : "Detalhes da viagem"
          }
          closed={!open}
          onClose={() => {
            navigate({ pathname: "/routing/performance/list", search: `?page=${location.state?.page}` });
          }}
          position={Sidebar.RIGHT}
          width={520}
          headerClassName="padding-left-15"
        >
          <div>
            <SidebarTripDetail
              tripDetails={tripDetails}
              usersAmity={usersAmity}
              followers={followers}
              driverId={driverId}
              onClose={() => navigate("/routing/trips")}
              handleStopItem={async (s) => {
                if (s?.id) {
                  try {
                    const response = await getCheckpointDetailByIdV2(s.id);
                    setCheckpoint(response?.data?.result ?? s);
                  } catch {
                    setCheckpoint(s);
                  }
                } else {
                  setCheckpoint(s);
                }
                setOpen(false);
                setOpenStages(true);
              }}
            />
          </div>
        </Sidebar>

        <SidebarCheckpoint
          open={!openStages}
          data={checkpoint}
          onClose={() => {
            setOpenStages(false);
            setOpen(true);
          }}
          onClickItemInfo={(stage) => {
            setStage(stage)
            setOpenDialog(true)
          }}
        />

        <Dialog.Checkpoint
          totalStages={checkpoint?.steps?.length || 0}
          open={openDialog}
          stage={stage}
          onClose={() => {
            setOpenDialog(false);
          }}
          tripId={tripId ?? undefined}
          checkpointId={checkpoint?.id}
          checkpointStop={checkpoint}
        />

      </ApplicationLayout.Sidebar>


      <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct">
        <MapDrawingProvider>
          <RioMap
            zoom={zoom}
            position={position}
            boundingBox={boundingBox}
            apiKey={process.env.REACT_APP_RIO_MAP_KEY as string}
            segments={
              segments.length ? [{ points: segments, alternative: false }] : []
            }
            currencySegments={
              currencySegments.length ? [{ points: currencySegments, alternative: false }] : []
            }
            markers={currencyMarker ? [...markers, currencyMarker] : markers}
            useClusters={true}
          />
        </MapDrawingProvider>
      </ApplicationLayout.Body>

      {!tripDetails && loading ? (
        <div className="display-flex align-items-center justify-content-center width-100pct height-100pct">
          <NotFoundState
            headline='Detalhes da rota'
            message={'Não foi possível obter os detalhes da rota, por favor tente novamente.'}
            condensed
            buttons={[
              {
                text: (
                  <>
                    <span className='rioglyph rioglyph-close' />
                    <span>Sair</span>
                  </>
                ),
                className: 'foobar width-200',
                bsStyle: 'default',
                onClick: () => navigate('/routing/trips/performance/list'),
              },
              {
                text: (
                  <>
                    <span className='rioglyph rioglyph-refresh spinning' />
                    <span>Atualizar</span>
                  </>
                ),
                className: 'foobar width-200',
                onClick: () => loadTripDetails(driverId as string, tripId as string),
              },
            ]}
          />
        </div>
      ) : null}

    </>
  )
}

export default PerformanceScreen;